/* eslint-disable @typescript-eslint/member-ordering */
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ReplaySubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { TimeSlotConfiguration } from '../models/timeSlotConfiguration';
import { TimeSlotGenerationOutputDTO } from '../models/timeSlotDTO';
import { TimeSlotGenerationDTO } from '../models/TimeSlotGenerationDTO';
import { CalendarWeek } from '../models/CalendarWeek';
import { CalendarSaveGeneratedInput } from '../models/CalendarSaveGeneratedInput';
import { CalendarWeekDay } from '../models/CalendarWeekDay';
import { CalendarSlotCell } from '../models/CalendarSlotCell';
import { CalendarSlotState } from '../models/CalendarSlotState';
import { BirthdayZone } from '../models/birthdayZone';


@Injectable({ providedIn: 'root' })
export class TimeSlotConfigurationService {
  apiUrl = environment.apiUrl;

  private timeSlotsSource = new ReplaySubject<TimeSlotConfiguration[]>(1);
  timeSlots$ = this.timeSlotsSource.asObservable();

  constructor(private http: HttpClient) { }

  addTimeSlotConfiguration(config: Omit<TimeSlotConfiguration, 'id' | 'room'>): Observable<TimeSlotConfiguration> {
    return this.http.post<TimeSlotConfiguration>(`${this.apiUrl}timeslotconfiguration`, config);
  }

  getAllTimeSlotConfigurations(): Observable<TimeSlotConfiguration[]> {
    return this.http.get<TimeSlotConfiguration[]>(`${this.apiUrl}timeslotconfiguration`)
      .pipe(tap(configs => { if (configs) this.timeSlotsSource.next(configs); }));
  }

  editTimeSlotConfiguration(config: TimeSlotConfiguration): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}timeslotconfiguration/${config.id}`, config);
  }

  deleteTimeSlotConfiguration(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}timeslotconfiguration/${id}`);
  }

  generateTimeSlots(payload: TimeSlotGenerationDTO): Observable<TimeSlotGenerationOutputDTO[]> {
    return this.http.post<TimeSlotGenerationOutputDTO[]>(
      `${this.apiUrl}timeslotconfiguration/generate`,
      payload
    );
  }

  getWeekCalendar(startDate: Date, roomId: number, birthdayZoneId?: number): Observable<CalendarWeek> {
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let params = new HttpParams()
      .set('startDate', this.formatDateOnly(startDate))
      .set('roomId', roomId)
      .set('localTimeZone', localTimeZone);

    if (birthdayZoneId != null) {
      params = params.set('birthdayZoneId', birthdayZoneId);
    }

    return this.http
      .get<any>(`${this.apiUrl}timeslotconfiguration/week-calendar`, { params })
      .pipe(map(json => this.mapWeekCalendar(json)));
  }


  generateOverlay(input: TimeSlotGenerationDTO): Observable<CalendarWeek> {
    return this.http
      .post<any>(`${this.apiUrl}timeslotconfiguration/generate-overlay`, input)
      .pipe(map(json => this.mapWeekCalendar(json)));
  }

  saveGenerated(input: CalendarSaveGeneratedInput): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}timeslotconfiguration/save`, input);
  }

  private formatDateOnly(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

 private mapWeekCalendar(x: any): CalendarWeek {
  const wc = new CalendarWeek();
  wc.weekStart = x?.weekStart ?? '';
  wc.weekEnd = x?.weekEnd ?? '';
  wc.days = (x?.days ?? []).map((d: any) => {
    const wd = new CalendarWeekDay();
    wd.dayOfWeek = d?.dayOfWeek ?? '';
    wd.date = d?.date ?? '';
    wd.slots = (d?.slots ?? []).map((s: any) => {
      const sc = new CalendarSlotCell();
      sc.time = s?.time ?? '';
      sc.state = (s?.state ?? 0) as CalendarSlotState;
      sc.timeSlotConfigurationId = s?.timeSlotConfigurationId ?? null; 
      return sc;
    });
    return wd;
  });
  return wc;
}


  getZonesByRoom(roomId: number, roomName: string) {
    let params = new HttpParams()
      .set('RoomNameQuery', roomName)
      .set('pageNumber', 1)
      .set('pageSize', 1000);

    return this.http.get<BirthdayZone[]>(
      `${this.apiUrl}BirthDayZone/SearchBirthdayZones`,
      { params }
    );
  }

  getCalendarWithAppointments(startDate: Date, roomId: number, birthdayZoneId?: number): Observable<CalendarWeek> {
  const startDateStr = this.formatDateOnly(startDate);
  let params = new HttpParams()
    .set('startDate', startDateStr)
    .set('roomId', roomId)
    .set('localTimeZone', Intl.DateTimeFormat().resolvedOptions().timeZone);

  if (birthdayZoneId != null) {
    params = params.set('birthdayZoneId', birthdayZoneId);
  }

  const url = `${this.apiUrl}timeslotconfiguration/calendar-with-appointments`;

  return this.http
    .get<any>(url, { params })
    .pipe(
      map(json => this.mapWeekCalendar(json)),
    );
}

resolveSlot(
  dateIso: string,
  roomId: number,
  zoneId: number | null,
  timeLabel: string
): Observable<{ start: string; end: string }> {
  let params = new HttpParams()
    .set('date', dateIso)
    .set('roomId', roomId)
    .set('timeLabel', timeLabel);

  if (zoneId != null) {
    params = params.set('birthdayZoneId', zoneId);
  }

  return this.http
    .get<any>(`${this.apiUrl}timeslotconfiguration/resolve`, { params })
    .pipe(
      map(res => ({
        start: res?.start ?? res?.Start,
        end:   res?.end   ?? res?.End,
      }))
    );
}

}
